/**
 * Authentication Service
 *
 * Handles all authentication-related API calls.
 * Login, logout, token management - the works! 🔒
 */

import api, { setAuthToken, clearAuthData, setLoggingOut } from "./api";
import {
  CACHE_KEYS,
  clearCachedValue,
  writeCachedValue,
} from "../utils/localCache";

const authService = {
  login: async (email, password) => {
    try {
      const response = await api.post("/auth/login/", {
        email,
        password,
      });

      const { tokens, user } = response.data;
      const { access, refresh } = tokens;

      // Store tokens
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);

      // Store user info
      if (user) {
        clearCachedValue(CACHE_KEYS.profilePage);
        localStorage.setItem("user", JSON.stringify(user));
        writeCachedValue(CACHE_KEYS.minimalProfile, user);
      }

      console.log("✅ Login successful!");
      return response.data;
    } catch (error) {
      console.error("❌ Login failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @returns {Promise} Response data
   */
  register: async (userData) => {
    try {
      const response = await api.post("/auth/register/", userData);
      console.log("✅ Registration successful!");
      return response.data;
    } catch (error) {
      console.error("❌ Registration failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Verify OTP (Universal - works for both registration and password reset!)
   * @param {string} email - User email
   * @param {string} otp - OTP code
   * @param {string} purpose - OTP purpose: 'registration' or 'password_reset'
   * @returns {Promise} Response data
   */
  verifyOTP: async (email, otp, purpose = "registration") => {
    try {
      const response = await api.post("/auth/verify-otp/", {
        email,
        otp,
        purpose, // ✅ Send purpose to backend!
      });
      console.log(`✅ OTP Verification successful! (purpose: ${purpose})`);
      return response.data;
    } catch (error) {
      console.error("❌ OTP Verification failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Resend OTP (Registration flow)
   * @param {string} email - User email
   * @returns {Promise} Response data
   */
  resendOTP: async (email) => {
    try {
      const response = await api.post("/auth/resend-otp/", { email });
      console.log("✅ OTP Resend successful!");
      return response.data;
    } catch (error) {
      console.error("❌ OTP Resend failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Set Password (After registration OTP verification)
   * @param {string} email - User email
   * @param {string} password - New password
   * @param {string} confirmPassword - Confirm password
   * @returns {Promise} Response data
   */
  setPassword: async (email, password, confirmPassword) => {
    try {
      const response = await api.post("/auth/set-password/", {
        email,
        password,
        confirm_password: confirmPassword,
      });
      console.log("✅ Password Set successful!");
      return response.data;
    } catch (error) {
      console.error("❌ Set Password failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Request Password Reset (Sends OTP with purpose='password_reset')
   * @param {string} email - User email
   * @returns {Promise} Response data
   */
  forgotPassword: async (email) => {
    try {
      const response = await api.post("/auth/forgot-password/", { email });
      console.log("✅ Password reset OTP sent!");
      return response.data;
    } catch (error) {
      console.error("❌ Forgot password failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Reset Password (After password reset OTP verification)
   * @param {string} email - User email
   * @param {string} otp - Verified OTP code
   * @param {string} password - New password
   * @param {string} confirmPassword - Confirm password
   * @returns {Promise} Response data
   */
  resetPassword: async (email, otp, password, confirmPassword) => {
    try {
      const response = await api.post("/auth/reset-password/", {
        email,
        otp, // ✅ Send OTP for verification
        new_password: password, // ✅ Backend expects 'new_password'
        confirm_password: confirmPassword,
      });
      console.log("✅ Password reset successful!");
      return response.data;
    } catch (error) {
      console.error("❌ Reset password failed:", error.response?.data);
      throw error;
    }
  },

  /**
   * Logout user
   */
  logout: async () => {
    // 1. Mark as logging out immediately to squash any pending 401s
    setLoggingOut(true);

    try {
      const refreshToken = localStorage.getItem("refresh_token");
      const hasPin = !!localStorage.getItem("encrypted_refresh_token");
      
      // 2. Perform background logout if refresh token exists AND no PIN is set
      // (If a PIN is set, logout acts as "Lock", so we keep the token alive on the server)
      if (refreshToken && !hasPin) {
        // We're intentionally NOT AWAITING this to make logout feel instant! ⚡
        api.post("/auth/logout/", { refresh: refreshToken }).catch(err => {
          console.debug("Background logout API call failed (this is usually fine):", err.message);
        });
      }
    } catch (err) {
      console.warn("Error getting refresh token for logout:", err.message);
    } finally {
      // 3. Clear all local data immediately
      clearAuthData();
      localStorage.removeItem("user");
      clearCachedValue(CACHE_KEYS.minimalProfile);
      clearCachedValue(CACHE_KEYS.profilePage);
      
      // We explicitly DO NOT remove "encrypted_refresh_token" or "pin_user" here
      // so the user can log back in with their PIN.

      console.log("👋 Optimistic logout complete (background cleanup started)");
      
      // Keep the isLoggingOut flag for 2 seconds to squash near-simultaneous 401s
      setTimeout(() => setLoggingOut(false), 2000);
    }
  },

  /**
   * Get current user info
   * @returns {Object|null} User object or null
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem("access_token");
  },

  /**
   * Refresh access token
   * @returns {Promise} New access token
   */
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await api.post("/auth/token/refresh/", {
        refresh: refreshToken,
      });

      const { access } = response.data;
      localStorage.setItem("access_token", access);

      return access;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      authService.logout();
      throw error;
    }
  },
};

export default authService;
